import { useCallback, useState } from 'react';
import type { AppointmentReasonRef, AppointmentReasonDraft } from '@domain/entities';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import { ConfirmModal, ErrorState, Modal, RefreshButton, StatusBadge, TableSkeleton, TextField } from '../components/ui';

const NEW_DRAFT: AppointmentReasonDraft = { slug: '', label: '', sortOrder: 0 };

export function AppointmentReasonsPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vAppointmentReasons = useRealtimeVersion('appointment-reasons');

  const isMobile = useIsMobile();
  const reasons = useAsync(() => container.appointmentReasons.list.execute(), [container, vAppointmentReasons]);
  const [editorId, setEditorId] = useState<string | null | undefined>(undefined);
  const [draft, setDraft] = useState<AppointmentReasonDraft>(NEW_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const openEditor = useCallback((reason: AppointmentReasonRef | null) => {
    if (reason === null) { setEditorId(null); setDraft(NEW_DRAFT); return; }
    setEditorId(reason.id);
    setDraft({ slug: reason.slug, label: reason.label, sortOrder: reason.sortOrder });
  }, []);

  const save = useCallback(async () => {
    if (editorId === undefined) return;
    setSaving(true);
    try {
      await container.appointmentReasons.save.execute({ id: editorId, draft });
      toast.notify(editorId === null ? 'Motif ajouté' : 'Motif mis à jour');
      setEditorId(undefined);
      reasons.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editorId, draft, toast, reasons]);

  const toggle = useCallback(async (reason: AppointmentReasonRef) => {
    try {
      await container.appointmentReasons.toggle.execute({ id: reason.id, currentIsActive: reason.isActive });
      reasons.reload();
    } catch (error) { toast.notifyError(error); }
  }, [container, toast, reasons]);

  const remove = useCallback(async (id: string) => {
    try {
      await container.appointmentReasons.remove.execute(id);
      toast.notify('Motif supprimé');
      reasons.reload();
    } catch (error) { toast.notifyError(error); }
  }, [container, toast, reasons]);

  const rows = reasons.data ?? [];

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <div className="bbm-toolbar__hint">
            Motifs proposés lors de la création d'un rendez-vous
          </div>
        </div>
        <div className="bbm-toolbar__end">
          <button
            type="button"
            className="bbm-btn bbm-btn--primary"
            disabled={!can('fruits', 'create')}
            title={can('fruits', 'create') ? '' : 'Réservé au super-admin'}
            onClick={() => openEditor(null)}
          >
            + Ajouter un motif
          </button>
          <RefreshButton onRefresh={reasons.reload} loading={reasons.loading} />
        </div>
      </div>

      {reasons.loading ? <TableSkeleton rows={6} /> : null}
      {reasons.error ? <ErrorState status={reasons.error.status} message={reasons.error.message} onRetry={reasons.reload} /> : null}

      {rows.length > 0 ? (
        isMobile ? (
          <div className="bbm-card-stack">
            {rows.map((reason) => (
              <div key={reason.id} className="bbm-mobile-card">
                <div className="bbm-mobile-card__head">
                  <div>
                    <div className="bbm-mobile-card__title">{reason.label}</div>
                    <div className="bbm-mobile-card__sub" style={{ fontFamily: 'monospace' }}>{reason.slug}</div>
                  </div>
                  <StatusBadge status={reason.isActive ? 'approved' : 'inactive'} label={reason.isActive ? 'Actif' : 'Inactif'} />
                </div>
                <div className="bbm-mobile-card__fields">
                  <div className="bbm-mobile-card__field">
                    <span className="bbm-mobile-card__label">Ordre</span>
                    <span>{reason.sortOrder}</span>
                  </div>
                </div>
                <div className="bbm-mobile-card__actions">
                  <button type="button" className="bbm-btn" disabled={!can('fruits', 'edit')} onClick={() => setPending({
                    title: 'Modifier la visibilité ?',
                    body: 'La visibilité de ce motif sera modifiée.',
                    fn: () => void toggle(reason),
                  })}>{reason.isActive ? 'Désactiver' : 'Activer'}</button>
                  <button type="button" className="bbm-btn" disabled={!can('fruits', 'edit')} onClick={() => openEditor(reason)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('fruits', 'delete')} onClick={() => setPending({
                    title: 'Supprimer ce motif ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(reason.id),
                  })}>Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bbm-panel">
            <div className="bbm-row bbm-row--head">
              <div style={{ width: 60 }}>Ordre</div>
              <div style={{ width: 200 }}>Libellé</div>
              <div style={{ width: 200 }}>Slug</div>
              <div style={{ width: 100 }}>Statut</div>
              <div style={{ marginLeft: 'auto' }}>Actions</div>
            </div>
            {rows.map((reason) => (
              <div key={reason.id} className="bbm-row">
                <div className="bbm-cell" style={{ width: 60 }}>{reason.sortOrder}</div>
                <div className="bbm-cell-strong" style={{ width: 200 }}>{reason.label}</div>
                <div className="bbm-cell" style={{ width: 200, fontFamily: 'monospace', fontSize: 12 }}>{reason.slug}</div>
                <div style={{ width: 100 }}>
                  <StatusBadge status={reason.isActive ? 'approved' : 'inactive'} label={reason.isActive ? 'Actif' : 'Inactif'} />
                </div>
                <div className="bbm-row__actions">
                  <button type="button" className="bbm-btn" disabled={!can('fruits', 'edit')} onClick={() => setPending({
                    title: 'Modifier la visibilité ?',
                    body: 'La visibilité de ce motif sera modifiée.',
                    fn: () => void toggle(reason),
                  })}>
                    {reason.isActive ? 'Désactiver' : 'Activer'}
                  </button>
                  <button type="button" className="bbm-btn" disabled={!can('fruits', 'edit')} onClick={() => openEditor(reason)}>Modifier</button>
                  <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('fruits', 'delete')} onClick={() => setPending({
                    title: 'Supprimer ce motif ?',
                    body: 'Cette action est irréversible.',
                    confirmLabel: 'Supprimer',
                    danger: true,
                    fn: () => void remove(reason.id),
                  })}>
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : null}

      {editorId !== undefined ? (
        <Modal
          title={editorId === null ? 'Nouveau motif' : 'Modifier le motif'}
          width={480}
          onClose={() => setEditorId(undefined)}
          onSubmit={() => void save()}
          busy={saving}
        >
          <TextField label="Libellé" value={draft.label} onChange={(v) => setDraft({ ...draft, label: v })} />
          <TextField label="Slug (lettres minuscules, tirets)" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} />
          <TextField label="Ordre d'affichage" value={String(draft.sortOrder)} onChange={(v) => setDraft({ ...draft, sortOrder: Number(v) || 0 })} />
        </Modal>
      ) : null}
      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}
